import { useEffect, useMemo, useRef } from "react";
import * as Sentry from "@sentry/react";
import { useMutation, useQuery, useQueryClient, type UseQueryResult } from "@tanstack/react-query";
import { api } from "@/lib/api-client";
import type {
  CanvasEdge,
  CanvasNode,
  CanvasViewport,
  UpdateCanvasRequest,
} from "@opus-dt/shared-types";
import type { LineCanvas } from "../../../types/canvas.types.js";
import { useDraftStore } from "../draft/useLineDraftState";
import { draftToLineCanvas, isDraftLineId } from "./draft-canvas-adapter";

export type { LineCanvas } from "../../../types/canvas.types.js";
export { DRAFT_LINE_SENTINEL } from "./draft-canvas-adapter";

export function canvasQueryKey(lineId: string) {
  return ["canvas", lineId] as const;
}

export function useLineCanvas(lineId: string, enabled = true): UseQueryResult<LineCanvas> {
  const isDraft = isDraftLineId(lineId);
  const draftCanvas = useDraftStore((s) => s.canvas);
  const draftMachines = useDraftStore((s) => s.machines);
  const draftData = useMemo<LineCanvas | undefined>(() => {
    if (!isDraft) return undefined;
    return draftToLineCanvas({
      header: { name: "", description: "", responsible: "", productionUnit: "" },
      canvas: draftCanvas,
      machines: draftMachines,
      dirty: false,
    });
  }, [isDraft, draftCanvas, draftMachines]);

  const serverQuery = useQuery<LineCanvas>({
    queryKey: canvasQueryKey(lineId),
    queryFn: async () => {
      const data = await api.get<{
        nodes: CanvasNode[];
        edges: CanvasEdge[];
        viewport: CanvasViewport | null;
        isFallback: boolean;
      }>(`/api/v1/lines/${lineId}/canvas`);
      return {
        nodes: data.nodes,
        edges: data.edges,
        viewport: data.viewport ?? null,
        isFallback: data.isFallback,
      };
    },
    enabled: enabled && !!lineId && !isDraft,
    staleTime: 60_000,
  });

  if (isDraft) {
    return {
      data: draftData,
      isLoading: false,
      isFetching: false,
      isError: false,
      isSuccess: draftData !== undefined,
      error: null,
      refetch: () => Promise.resolve({ data: draftData }),
      status: "success",
    } as unknown as UseQueryResult<LineCanvas>;
  }

  return serverQuery;
}

export function useUpdateCanvas(lineId: string) {
  const qc = useQueryClient();
  const setDraftCanvas = useDraftStore((s) => s.setCanvas);
  const isDraft = isDraftLineId(lineId);
  return useMutation({
    mutationFn: async (input: UpdateCanvasRequest & { confirmRealLine?: boolean }) => {
      if (isDraft) {
        const draftNodes = input.nodes.map((n) => ({
          tmpId: ensureTmpId(n.id, "node"),
          type: n.type,
          label: n.label,
          x: n.x,
          y: n.y,
          machineTmpId:
            n.type === "MACHINE" && typeof n.data?.machineId === "string" ? n.data.machineId : null,
          data: stripMachineId(n.data ?? {}),
        }));
        const draftEdges = input.edges.map((e) => ({
          tmpId: ensureTmpId(e.id, "edge"),
          sourceNodeTmpId: e.sourceNodeId as `tmp_node_${string}`,
          targetNodeTmpId: e.targetNodeId as `tmp_node_${string}`,
          type: e.type,
          data: e.data ?? {},
        }));
        setDraftCanvas({ nodes: draftNodes, edges: draftEdges });
        return { ok: true };
      }
      const { confirmRealLine, ...body } = input;
      return api.patch(`/api/v1/lines/${lineId}/canvas`, body, {
        headers: confirmRealLine ? { "X-Confirm-Real-Line": "true" } : undefined,
      });
    },
    onSuccess: () => {
      if (!isDraft) {
        void qc.invalidateQueries({ queryKey: canvasQueryKey(lineId) });
      }
    },
    // Tratado localmente em ProductionLineCanvas/hooks/useCanvasSaveHandler.ts.
    meta: { skipErrorToast: true },
  });
}

function ensureTmpId<K extends "node" | "edge">(id: string, kind: K): `tmp_${K}_${string}` {
  const prefix = `tmp_${kind}_` as const;
  if (id.startsWith(prefix)) return id as `tmp_${K}_${string}`;
  const uuid = id.replace(/^tmp_[a-z_]+_/, "");
  return `${prefix}${uuid}`;
}

function stripMachineId(data: Record<string, unknown>): Record<string, unknown> {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- destruct para excluir machineId
  const { machineId, ...rest } = data;
  return rest;
}

export function useDebouncedViewportSave(lineId: string, delayMs = 1000) {
  const qc = useQueryClient();
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingRef = useRef<CanvasViewport | null>(null);
  const isDraft = isDraftLineId(lineId);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  if (isDraft) {
    return () => {};
  }

  function flushSave() {
    const viewport = pendingRef.current;
    if (!viewport) return;
    pendingRef.current = null;
    const cached = qc.getQueryData<LineCanvas>(canvasQueryKey(lineId));
    if (!cached) return;
    void api
      .patch(`/api/v1/lines/${lineId}/canvas`, {
        nodes: cached.nodes,
        edges: cached.edges,
        viewport,
      })
      .then(() => {
        qc.setQueryData<LineCanvas>(canvasQueryKey(lineId), (prev) =>
          prev ? { ...prev, viewport } : prev
        );
      })
      // Autosave de viewport (cosmético), não vale toast a cada falha, mas não pode
      // ficar 100% invisível: reporta ao Sentry.
      .catch((err) => {
        Sentry.captureException(err);
      });
  }

  function scheduleSave(viewport: CanvasViewport) {
    pendingRef.current = viewport;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(flushSave, delayMs);
  }

  return scheduleSave;
}
